import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Heart, RefreshCw, Search, Loader2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import PaginationBar from '../../components/PaginationBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LIMIT = 50;

const VIEWER_TYPE_LABEL = {
  ps_user: 'Parichay Sangam',
  family_user: 'Family',
  professional: 'Professional',
  institutional: 'Institutional',
  admin: 'Admin (preview)',
};

const PSInterestActivity = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/ps/admin/interest-log`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { search, limit: LIMIT, skip: page * LIMIT },
      });
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load interest activity');
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const onSearch = (e) => {
    e?.preventDefault?.();
    setPage(0);
    setSearch(searchInput.trim());
  };

  const onReset = () => {
    setSearchInput('');
    setPage(0);
    setSearch('');
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="ps-interest-activity-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Heart className="w-6 h-6 text-pink-600" /> Interest Activity
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Express Interest log — total entries: <span className="font-semibold text-gray-700">{total}</span>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchRows} disabled={loading} data-testid="interest-refresh">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <form onSubmit={onSearch} className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by viewer email, candidate code (e.g. C205), or family code (e.g. F003)…"
            className="pl-9"
            data-testid="interest-search-input"
          />
        </div>
        <Button type="submit" disabled={loading} className="bg-pink-600 hover:bg-pink-700" data-testid="interest-search-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
        </Button>
        {search && (
          <Button type="button" variant="outline" onClick={onReset} data-testid="interest-clear-btn">Clear</Button>
        )}
      </form>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-[11px] tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">When</th>
                <th className="text-left px-4 py-2.5">Viewer</th>
                <th className="text-left px-4 py-2.5">Viewer Family</th>
                <th className="text-left px-4 py-2.5">Portal</th>
                <th className="text-left px-4 py-2.5">Candidate</th>
                <th className="text-left px-4 py-2.5">Code</th>
                <th className="text-left px-4 py-2.5">Family</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && !loading && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No interest activity matches your search.</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={i} className="hover:bg-pink-50/40" data-testid={`interest-row-${i}`}>
                  <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap">{fmt(r.timestamp)}</td>
                  <td className="px-4 py-2.5">
                    <span className="text-gray-900 font-medium break-all">{r.viewerEmail || '—'}</span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs">
                    {r.viewerType === 'ps_user' && r.viewerFamilyCode ? (
                      <span className="inline-block px-2 py-0.5 rounded bg-pink-50 text-pink-700 border border-pink-200" data-testid={`viewer-family-${i}`}>
                        {r.viewerFamilyCode}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-600">{VIEWER_TYPE_LABEL[r.viewerType] || r.viewerType || '—'}</td>
                  <td className="px-4 py-2.5 text-gray-800">{r.candidateName || '—'}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-orange-700">{r.candidateCode}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-600">{r.candidateFamilyCode || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-4 py-3 bg-gray-50 border-t">
            <PaginationBar
              currentPage={page + 1}
              totalPages={totalPages}
              onPageChange={(p) => setPage(p - 1)}
              totalItems={total}
              pageSize={LIMIT}
              itemLabel="records"
              testIdPrefix="interest"
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default PSInterestActivity;
